// Pure helpers for notification docs, shared by the bell count (UnreadContext),
// the Notifications page and the chats badge. No service imports (node-testable).
//
// The effective type is `data.type || type`, as utils/notificationRouting.js
// reads it: DM notices are top-level "conversation_message" with data.type
// dm_request | dm_message | inbox_message.

const effectiveType = (n) => (n && (n.data?.type || n.type)) || "";

/** Chat-message notices. They are marked read only from the bell, so they never count there. */
const CHAT_MESSAGE_TYPES = new Set(["club_message", "event_message", "subchat_message", "dm_message", "inbox_message"]);

/** Mobile rule (NotificationsScreen.js): unread while neither `read` nor `read_at` is set. */
export const isUnread = (n) => !!n && !n.read && !n.read_at;

/**
 * Counts toward the bell: unread, not a silent connection-note DM notice, and
 * not a chat-message notice (those would grow without bound; see
 * contexts/UnreadChatsContext.js).
 */
export const isBellCountable = (n) => isUnread(n) && n.silent !== true && !CHAT_MESSAGE_TYPES.has(effectiveType(n));

/** Inline-actions kind for a notification row: "connection" | "dm" | null. */
export function requestKind(n) {
  const t = effectiveType(n);
  if (t === "connection_request") return "connection";
  if (t === "dm_request") return "dm";
  return null;
}

/** Icon name (web/src/components/Icon.jsx) for a notification row. */
export function iconFor(n) {
  const t = effectiveType(n);
  const top = n?.type || "";
  if (t.startsWith("connection_")) return "userplus";
  if (t.startsWith("dm_") || t === "club_message" || t === "inbox_message" || t.startsWith("subchat_")) return "chat";
  if (top === "super_admin_club_creation_requests" || t === "club_creation_request") return "plus";
  if (top === "super_admin_admin_requests" || t === "admin_request") return "shield";
  if (t === "club_application") return "users";
  if (t === "club_of_the_week_won") return "star";
  return "cal";
}

/** Tile tone for a notification row: blue | green | amber | purple. */
export function toneFor(n) {
  const t = effectiveType(n);
  if (t === "connection_accepted" || t === "application_approved" || t === "admin_access_granted") return "green";
  if (t === "club_of_the_week_won") return "amber";
  if (t === "club_creation_request" || t === "admin_request" || (n?.type || "").startsWith("super_admin_")) return "purple";
  return "blue";
}
